import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Calendar,
  FileText,
  Lock,
  QrCode,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Button } from '@components/common/Button';
import { Badge } from '@components/common/Badge';
import { getLicenseByHashCode } from '@mocks/verification';
import { LICENSE_CATEGORY_LABELS } from '@utils/constants';
import type { VerifiableLicense } from '@mocks/verification';

export const LicenseVerification: React.FC = () => {
  const { hashcode } = useParams<{ hashcode: string }>();
  const navigate = useNavigate();
  const [license, setLicense] = useState<VerifiableLicense | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simulate API call delay
    setTimeout(() => {
      if (!hashcode) {
        setError('No verification code provided');
        setLoading(false);
        return;
      }

      const foundLicense = getLicenseByHashCode(hashcode);
      
      if (!foundLicense) {
        setError('Invalid verification code. This license is not approved for verification.');
        setLoading(false);
        return;
      }

      setLicense(foundLicense);
      setLoading(false);
    }, 800);
  }, [hashcode]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Active</Badge>;
      case 'EXPIRED':
        return <Badge variant="warning" icon={<AlertTriangle className="h-3 w-3" />}>Expired</Badge>;
      case 'SUSPENDED':
        return <Badge variant="warning" icon={<AlertTriangle className="h-3 w-3" />}>Suspended</Badge>;
      case 'REVOKED':
        return <Badge variant="danger" icon={<XCircle className="h-3 w-3" />}>Revoked</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-ncaa-primary mx-auto mb-4" />
            <p className="text-gray-600">Verifying license...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !license) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <div className="text-center py-12">
            <XCircle className="h-20 w-20 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
            <p className="text-gray-600 mb-6">
              {error || 'This license is not approved for verification.'}
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                <strong>Error Code:</strong> INVALID_HASH
              </p>
              <p className="text-sm text-red-800 mt-1">
                Please ensure you scanned the correct QR code or contact NCAA for assistance.
              </p>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              Return to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <QrCode className="h-8 w-8" />
            <h1 className="text-3xl font-bold">DCALV</h1>
          </div>
          <p className="text-blue-100">Digital Civil Aviation License Verification Portal</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Verification Success */}
        <Card className="mb-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">License Verified</h2>
              <p className="text-gray-600">
                This license has been found in the NCAA database and is approved for verification.
              </p>
            </div>
          </div>

          {/* Basic License Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-600 mb-1">License Number</p>
              <p className="font-semibold text-gray-900 font-mono">{license.license_number}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">License Holder</p>
              <p className="font-semibold text-gray-900">{license.holder_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">License Category</p>
              <p className="font-semibold text-gray-900">
                {LICENSE_CATEGORY_LABELS[license.license_category]}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Status</p>
              {getStatusBadge(license.status)}
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Issue Date</p>
              <p className="font-medium text-gray-900">{formatDate(license.issue_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Expiry Date</p>
              <p className="font-medium text-gray-900">{formatDate(license.expiry_date)}</p>
            </div>
          </div>
        </Card>

        {/* Inspector Access Required */}
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-start gap-4">
            <Lock className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Full Details Require Authorization
              </h3>
              <p className="text-gray-700 mb-4">
                To view complete license details including photo ID, ratings, restrictions, and medical information,
                please log in as an authorized NCAA License Inspector.
              </p>
              <Button
                onClick={() => navigate(`/inspector/login?verify=${hashcode}`)}
                icon={<Shield className="h-4 w-4" />}
              >
                Inspector Login
              </Button>
            </div>
          </div>
        </Card>

        {/* Verification Info */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-2">
            <Shield className="h-4 w-4" />
            <span>Verified by Nigerian Civil Aviation Authority (NCAA)</span>
          </div>
          <p className="text-xs text-gray-400">
            This verification was performed on {new Date().toLocaleString('en-US')}
          </p>
        </div>
      </div>
    </div>
  );
};
